import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Info, Plus } from "lucide-react";
import { Switch } from "../../../common/Switch";
import { PriceFilter } from "../PriceFilter";
import { BedsAndBathsFilter } from "../BedsAndBathsFilter";
import { SqftFilter } from "../SqftFilter";

interface MoreFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  tempFilters: any;
  onTempFiltersChange: (filters: any) => void;
  onReset: () => void;
  onApply: () => void;
}

export const MoreFiltersModal: React.FC<MoreFiltersModalProps> = ({
  isOpen,
  onClose,
  tempFilters,
  onTempFiltersChange,
  onReset,
  onApply,
}) => {
  const handleApply = () => {
    onApply();
    onClose();
  };

  const handleReset = () => {
    // Get current window width
    const isDesktop = window.innerWidth >= 1024; // lg breakpoint is 1024px

    if (isDesktop) {
      // On desktop, only reset filters that are shown in the modal
      onTempFiltersChange({
        ...tempFilters,
        status: "active", // Reset to default active
        features: undefined,
        sqftRange: undefined,
        propertyType: "Single Family Residence", // Reset to default single family residence
        subdivision: undefined, // Reset subdivision
        zipCode: undefined, // Reset ZIP code
        county: undefined, // Reset county
        city: undefined, // Reset city
        mlsNumbers: undefined, // Reset MLS numbers
        publicremarks: undefined, // Reset keywords
        newconstructionyn: undefined, // Reset new construction filter
        rentalyn: undefined, // Reset rental property filter
        // Preserve price and beds/baths filters
        priceRange: tempFilters.priceRange,
        priceSort: tempFilters.priceSort,
        bedrooms: tempFilters.bedrooms,
        bathrooms: tempFilters.bathrooms,
        useExactMatchBeds: tempFilters.useExactMatchBeds,
        useExactMatchBaths: tempFilters.useExactMatchBaths,
      });
    } else {
      // On mobile, reset all filters but preserve default values for status and propertyType
      onReset();
      // After resetting, set the default values
      onTempFiltersChange({
        status: "active",
        propertyType: "Single Family Residence",
      });
    }
  };

  const portalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Create portal container if it doesn't exist
    if (!portalRef.current) {
      const div = document.createElement("div");
      div.style.position = "fixed";
      div.style.left = "0";
      div.style.top = "0";
      div.style.width = "100%";
      div.style.height = "0";
      div.style.zIndex = "9999";
      document.body.appendChild(div);
      portalRef.current = div;
    }

    return () => {
      // Cleanup on unmount
      if (portalRef.current?.parentElement) {
        portalRef.current.parentElement.removeChild(portalRef.current);
        portalRef.current = null;
      }
    };
  }, []);

  if (!isOpen) return null;

  return portalRef.current
    ? createPortal(
        <div className="fixed inset-0" onClick={onClose}>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          {/* Modal */}
          <div
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95%] sm:w-full max-w-2xl bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh] modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 border-b">
              <h3 className="text-lg font-bold">Filter Properties</h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-0 !bg-transparent"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8 overflow-y-auto custom-scrollbar">
              {/* Quick Filters - Only visible on mobile/tablet */}
              <div className="lg:hidden mb-6">
                {/* Price Filter */}
                <div className="mb-6">
                  <h3 className="text-sm lg:text-base font-bold text-gray-600 mb-3">
                    Price Range
                  </h3>
                  <PriceFilter
                    selectedMinPrice={tempFilters.priceRange?.min}
                    selectedMaxPrice={tempFilters.priceRange?.max}
                    sortBy={tempFilters.priceSort}
                    onMinPriceChange={(value) =>
                      onTempFiltersChange({
                        ...tempFilters,
                        priceRange: {
                          ...tempFilters.priceRange,
                          min: value,
                        },
                      })
                    }
                    onMaxPriceChange={(value) =>
                      onTempFiltersChange({
                        ...tempFilters,
                        priceRange: {
                          ...tempFilters.priceRange,
                          max: value,
                        },
                      })
                    }
                    onSortChange={(value) =>
                      onTempFiltersChange({
                        ...tempFilters,
                        priceSort: value,
                      })
                    }
                    hideButtons
                    fullWidth
                  />
                </div>

                {/* Beds & Baths Filter */}
                <div className="mb-6">
                  <h3 className="text-sm lg:text-base font-bold text-gray-600 mb-3">
                    Beds & Baths
                  </h3>
                  <BedsAndBathsFilter
                    selectedBedrooms={tempFilters.bedrooms}
                    selectedBathrooms={tempFilters.bathrooms}
                    useExactMatchBeds={tempFilters.useExactMatchBeds}
                    useExactMatchBaths={tempFilters.useExactMatchBaths}
                    onBedroomsChange={(value: string) =>
                      onTempFiltersChange({
                        ...tempFilters,
                        bedrooms: value,
                      })
                    }
                    onBathroomsChange={(value: string) =>
                      onTempFiltersChange({
                        ...tempFilters,
                        bathrooms: value,
                      })
                    }
                    onExactMatchBedsChange={(value: boolean) =>
                      onTempFiltersChange({
                        ...tempFilters,
                        useExactMatchBeds: value,
                      })
                    }
                    onExactMatchBathsChange={(value: boolean) =>
                      onTempFiltersChange({
                        ...tempFilters,
                        useExactMatchBaths: value,
                      })
                    }
                    hideButtons
                    fullWidth
                  />
                </div>
                <hr className="border-gray-200 my-6" />
              </div>

              {/* Status */}
              <div className="mb-6">
                <h3 className="text-sm lg:text-base font-bold text-gray-600 mb-3">
                  Status
                </h3>
                <div className="flex flex-col gap-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        value="active"
                        checked={
                          Array.isArray(tempFilters.status)
                            ? tempFilters.status.includes("active")
                            : tempFilters.status === "active"
                        }
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const currentStatuses = Array.isArray(
                            tempFilters.status
                          )
                            ? [...tempFilters.status]
                            : tempFilters.status
                            ? [tempFilters.status]
                            : [];

                          let newStatuses;
                          if (isChecked) {
                            // Add active if not already included
                            if (!currentStatuses.includes("active")) {
                              newStatuses = [...currentStatuses, "active"];
                            } else {
                              newStatuses = currentStatuses;
                            }
                          } else {
                            // Remove active if present
                            newStatuses = currentStatuses.filter(
                              (s) => s !== "active"
                            );
                            // Default to active if empty
                            if (newStatuses.length === 0) {
                              newStatuses = ["active"];
                            }
                          }

                          onTempFiltersChange({
                            ...tempFilters,
                            status: newStatuses,
                          });
                        }}
                        className="text-primary focus:ring-0"
                      />
                      <span className="text-sm font-medium">
                        Active Property
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        value="pending"
                        checked={
                          Array.isArray(tempFilters.status)
                            ? tempFilters.status.includes("pending")
                            : tempFilters.status === "pending"
                        }
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const currentStatuses = Array.isArray(
                            tempFilters.status
                          )
                            ? [...tempFilters.status]
                            : tempFilters.status
                            ? [tempFilters.status]
                            : [];

                          let newStatuses;
                          if (isChecked) {
                            // Add pending if not already included
                            if (!currentStatuses.includes("pending")) {
                              newStatuses = [...currentStatuses, "pending"];
                            } else {
                              newStatuses = currentStatuses;
                            }
                          } else {
                            // Remove pending if present
                            newStatuses = currentStatuses.filter(
                              (s) => s !== "pending"
                            );
                            // Default to active if empty
                            if (newStatuses.length === 0) {
                              newStatuses = ["active"];
                            }
                          }

                          onTempFiltersChange({
                            ...tempFilters,
                            status: newStatuses,
                          });
                        }}
                        className="text-primary focus:ring-0"
                      />
                      <span className="text-sm font-medium">
                        Pending Property
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        value="closed"
                        checked={
                          Array.isArray(tempFilters.status)
                            ? tempFilters.status.includes("closed")
                            : tempFilters.status === "closed"
                        }
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const currentStatuses = Array.isArray(
                            tempFilters.status
                          )
                            ? [...tempFilters.status]
                            : tempFilters.status
                            ? [tempFilters.status]
                            : [];

                          let newStatuses;
                          if (isChecked) {
                            // Add closed if not already included
                            if (!currentStatuses.includes("closed")) {
                              newStatuses = [...currentStatuses, "closed"];
                            } else {
                              newStatuses = currentStatuses;
                            }
                          } else {
                            // Remove closed if present
                            newStatuses = currentStatuses.filter(
                              (s) => s !== "closed"
                            );
                            // Default to active if empty
                            if (newStatuses.length === 0) {
                              newStatuses = ["active"];
                            }
                          }

                          onTempFiltersChange({
                            ...tempFilters,
                            status: newStatuses,
                          });
                        }}
                        className="text-primary focus:ring-0"
                      />
                      <span className="text-sm font-medium">
                        Closed Property
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Select multiple statuses to see properties in different
                    stages
                  </p>
                </div>
              </div>
              <hr className="border-gray-200 my-6" />

              {/* Property Type */}
              <div className="mb-6">
                <h3 className="text-sm lg:text-base font-bold text-gray-600 mb-3">
                  Property Type
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="propertyType"
                      value="Single Family Residence"
                      checked={
                        tempFilters.propertyType === "Single Family Residence"
                      }
                      onChange={() =>
                        onTempFiltersChange({
                          ...tempFilters,
                          propertyType: "Single Family Residence",
                        })
                      }
                      className="text-primary focus:ring-0"
                    />
                    <span className="text-sm font-medium">
                      Single Family Residence
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="propertyType"
                      value="Condominium"
                      checked={tempFilters.propertyType === "Condominium"}
                      onChange={() =>
                        onTempFiltersChange({
                          ...tempFilters,
                          propertyType: "Condominium",
                        })
                      }
                      className="text-primary focus:ring-0"
                    />
                    <span className="text-sm font-medium">Condominium</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="propertyType"
                      value="Townhouse"
                      checked={tempFilters.propertyType === "Townhouse"}
                      onChange={() =>
                        onTempFiltersChange({
                          ...tempFilters,
                          propertyType: "Townhouse",
                        })
                      }
                      className="text-primary focus:ring-0"
                    />
                    <span className="text-sm font-medium">Townhouse</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="propertyType"
                      value="Land"
                      checked={tempFilters.propertyType === "Land"}
                      onChange={() =>
                        onTempFiltersChange({
                          ...tempFilters,
                          propertyType: "Land",
                        })
                      }
                      className="text-primary focus:ring-0"
                    />
                    <span className="text-sm font-medium">Land</span>
                  </label>
                </div>
              </div>
              <hr className="border-gray-200 my-6" />

              {/* Property Characteristics */}
              <div className="mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      New Construction
                    </span>
                    <Switch
                      checked={tempFilters.newconstructionyn || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          newconstructionyn: checked,
                        })
                      }
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Rental Property</span>
                    <Switch
                      checked={tempFilters.rentalyn || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          rentalyn: checked,
                        })
                      }
                      size="sm"
                    />
                  </div>
                </div>
              </div>
              <hr className="border-gray-200 my-6" />

              {/* Property Address */}
              <div className="mb-6">
                <h3 className="text-sm lg:text-base font-bold text-gray-600 mb-3">
                  MLS Number
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Search by MLS Number
                    </div>
                    <div className="flex flex-col gap-1">
                      {/* Input for adding new MLS numbers */}
                      <div className="flex">
                        <input
                          type="text"
                          id="mls-number-input"
                          placeholder="Enter MLS number"
                          className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:border-primary"
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" &&
                              e.currentTarget.value.trim()
                            ) {
                              e.preventDefault();
                              const newValue = e.currentTarget.value.trim();
                              const currentMLSNumbers = Array.isArray(
                                tempFilters.mlsNumbers
                              )
                                ? tempFilters.mlsNumbers
                                : [];

                              if (!currentMLSNumbers.includes(newValue)) {
                                onTempFiltersChange({
                                  ...tempFilters,
                                  mlsNumbers: [...currentMLSNumbers, newValue],
                                });
                                e.currentTarget.value = "";
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="ml-2 p-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center"
                          onClick={() => {
                            const input = document.getElementById(
                              "mls-number-input"
                            ) as HTMLInputElement;
                            if (input && input.value.trim()) {
                              const newValue = input.value.trim();
                              const currentMLSNumbers = Array.isArray(
                                tempFilters.mlsNumbers
                              )
                                ? tempFilters.mlsNumbers
                                : [];

                              if (!currentMLSNumbers.includes(newValue)) {
                                onTempFiltersChange({
                                  ...tempFilters,
                                  mlsNumbers: [...currentMLSNumbers, newValue],
                                });
                                input.value = "";
                              }
                            }
                          }}
                          aria-label="Add MLS number"
                          title="Add MLS number"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Press Enter or click Add to add multiple MLS numbers
                      </p>

                      {/* Display existing MLS numbers as tags */}
                      {Array.isArray(tempFilters.mlsNumbers) &&
                        tempFilters.mlsNumbers.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {tempFilters.mlsNumbers.map(
                              (mls: string, index: number) => (
                                <div
                                  key={index}
                                  className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-xs"
                                >
                                  <span>{mls}</span>
                                  <button
                                    type="button"
                                    className="ml-1 text-gray-500 hover:text-gray-700 px-1 py-0 bg-transparent"
                                    onClick={() => {
                                      const newMLSNumbers = [
                                        ...tempFilters.mlsNumbers,
                                      ];
                                      newMLSNumbers.splice(index, 1);
                                      onTempFiltersChange({
                                        ...tempFilters,
                                        mlsNumbers:
                                          newMLSNumbers.length > 0
                                            ? newMLSNumbers
                                            : undefined,
                                      });
                                    }}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
              <hr className="border-gray-200 my-6" />

              <div className="mb-6">
                <h3 className="text-sm lg:text-base font-bold text-gray-600 mb-3">
                  Property Address
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      County
                    </div>
                    <input
                      type="text"
                      value={tempFilters.county || ""}
                      onChange={(e) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          county: e.target.value,
                        })
                      }
                      placeholder="Enter county name"
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:border-primary"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      City
                    </div>
                    <input
                      type="text"
                      value={tempFilters.city || ""}
                      onChange={(e) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          city: e.target.value,
                        })
                      }
                      placeholder="Enter city"
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:border-primary"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      ZIP Code
                    </div>
                    <input
                      type="text"
                      value={tempFilters.zipCode || ""}
                      onChange={(e) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          zipCode: e.target.value,
                        })
                      }
                      placeholder="Enter ZIP code"
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:border-primary"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Subdivision
                    </div>
                    <div className="flex flex-col gap-1">
                      {/* Input for adding new subdivision */}
                      <div className="flex">
                        <input
                          type="text"
                          id="subdivision-input"
                          placeholder="Enter subdivision name"
                          className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:border-primary"
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" &&
                              e.currentTarget.value.trim()
                            ) {
                              e.preventDefault();
                              const newValue = e.currentTarget.value.trim();
                              const currentSubdivisions = Array.isArray(
                                tempFilters.subdivision
                              )
                                ? tempFilters.subdivision
                                : tempFilters.subdivision
                                ? [tempFilters.subdivision]
                                : [];

                              if (!currentSubdivisions.includes(newValue)) {
                                onTempFiltersChange({
                                  ...tempFilters,
                                  subdivision: [
                                    ...currentSubdivisions,
                                    newValue,
                                  ],
                                });
                                e.currentTarget.value = "";
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="ml-2 p-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center"
                          onClick={() => {
                            const input = document.getElementById(
                              "subdivision-input"
                            ) as HTMLInputElement;
                            if (input && input.value.trim()) {
                              const newValue = input.value.trim();
                              const currentSubdivisions = Array.isArray(
                                tempFilters.subdivision
                              )
                                ? tempFilters.subdivision
                                : tempFilters.subdivision
                                ? [tempFilters.subdivision]
                                : [];

                              if (!currentSubdivisions.includes(newValue)) {
                                onTempFiltersChange({
                                  ...tempFilters,
                                  subdivision: [
                                    ...currentSubdivisions,
                                    newValue,
                                  ],
                                });
                                input.value = "";
                              }
                            }
                          }}
                          aria-label="Add subdivision"
                          title="Add subdivision"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Press Enter or click Add to add multiple subdivisions
                      </p>

                      {/* Display existing subdivisions as tags */}
                      {Array.isArray(tempFilters.subdivision) &&
                        tempFilters.subdivision.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {tempFilters.subdivision.map(
                              (sub: string, index: number) => (
                                <div
                                  key={index}
                                  className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-xs"
                                >
                                  <span>{sub}</span>
                                  <button
                                    type="button"
                                    className="ml-1 text-gray-500 hover:text-gray-700 px-1 py-0 bg-transparent"
                                    onClick={() => {
                                      const newSubdivisions = [
                                        ...tempFilters.subdivision,
                                      ];
                                      newSubdivisions.splice(index, 1);
                                      onTempFiltersChange({
                                        ...tempFilters,
                                        subdivision:
                                          newSubdivisions.length > 0
                                            ? newSubdivisions
                                            : undefined,
                                      });
                                    }}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
              <hr className="border-gray-200 my-6" />

              {/* Keywords */}
              <div className="mb-6">
                <h3 className="text-sm lg:text-base font-bold text-gray-600 mb-3">
                  Keywords
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Search by Keywords
                    </div>
                    <div className="flex flex-col gap-1">
                      {/* Input for adding new keywords */}
                      <div className="flex">
                        <input
                          type="text"
                          id="keywords-input"
                          placeholder="Enter keyword"
                          className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:border-primary"
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" &&
                              e.currentTarget.value.trim()
                            ) {
                              e.preventDefault();
                              const newValue = e.currentTarget.value.trim();
                              const currentKeywords = Array.isArray(
                                tempFilters.publicremarks
                              )
                                ? tempFilters.publicremarks
                                : tempFilters.publicremarks
                                ? [tempFilters.publicremarks]
                                : [];

                              if (!currentKeywords.includes(newValue)) {
                                onTempFiltersChange({
                                  ...tempFilters,
                                  publicremarks: [...currentKeywords, newValue],
                                });
                                e.currentTarget.value = "";
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="ml-2 p-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center"
                          onClick={() => {
                            const input = document.getElementById(
                              "keywords-input"
                            ) as HTMLInputElement;
                            if (input && input.value.trim()) {
                              const newValue = input.value.trim();
                              const currentKeywords = Array.isArray(
                                tempFilters.publicremarks
                              )
                                ? tempFilters.publicremarks
                                : tempFilters.publicremarks
                                ? [tempFilters.publicremarks]
                                : [];

                              if (!currentKeywords.includes(newValue)) {
                                onTempFiltersChange({
                                  ...tempFilters,
                                  publicremarks: [...currentKeywords, newValue],
                                });
                                input.value = "";
                              }
                            }
                          }}
                          aria-label="Add keyword"
                          title="Add keyword"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Add keywords to search in property descriptions (e.g.,
                        "waterfront", "renovated", "garage")
                      </p>

                      {/* Display existing keywords as tags */}
                      {Array.isArray(tempFilters.publicremarks) &&
                        tempFilters.publicremarks.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {tempFilters.publicremarks.map(
                              (keyword: string, index: number) => (
                                <div
                                  key={index}
                                  className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-xs"
                                >
                                  <span>{keyword}</span>
                                  <button
                                    type="button"
                                    className="ml-1 text-gray-500 hover:text-gray-700 px-1 py-0 bg-transparent"
                                    onClick={() => {
                                      const newKeywords = [
                                        ...tempFilters.publicremarks,
                                      ];
                                      newKeywords.splice(index, 1);
                                      onTempFiltersChange({
                                        ...tempFilters,
                                        publicremarks:
                                          newKeywords.length > 0
                                            ? newKeywords
                                            : undefined,
                                      });
                                    }}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
              <hr className="border-gray-200 my-6" />

              {/* Property Features */}
              <div className="mb-6">
                <h3 className="text-sm lg:text-base font-bold text-gray-600 mb-3">
                  Property Features
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Waterfront</span>
                    <Switch
                      checked={tempFilters.features?.waterfront || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          features: {
                            ...tempFilters.features,
                            waterfront: checked,
                          },
                        })
                      }
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Pool</span>
                    <Switch
                      checked={tempFilters.features?.pool || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          features: {
                            ...tempFilters.features,
                            pool: checked,
                          },
                        })
                      }
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Basement</span>
                    <Switch
                      checked={tempFilters.features?.basement || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          features: {
                            ...tempFilters.features,
                            basement: checked,
                          },
                        })
                      }
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Garage</span>
                    <Switch
                      checked={tempFilters.features?.garage || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          features: {
                            ...tempFilters.features,
                            garage: checked,
                          },
                        })
                      }
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">City View</span>
                    <Switch
                      checked={tempFilters.features?.cityView || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          features: {
                            ...tempFilters.features,
                            cityView: checked,
                          },
                        })
                      }
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Mountain View</span>
                    <Switch
                      checked={tempFilters.features?.mountainView || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          features: {
                            ...tempFilters.features,
                            mountainView: checked,
                          },
                        })
                      }
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Park View</span>
                    <Switch
                      checked={tempFilters.features?.parkView || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          features: {
                            ...tempFilters.features,
                            parkView: checked,
                          },
                        })
                      }
                      size="sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Water View</span>
                    <Switch
                      checked={tempFilters.features?.waterView || false}
                      onChange={(checked: boolean) =>
                        onTempFiltersChange({
                          ...tempFilters,
                          features: {
                            ...tempFilters.features,
                            waterView: checked,
                          },
                        })
                      }
                      size="sm"
                    />
                  </div>
                </div>
              </div>
              <hr className="border-gray-200 my-6" />

              {/* Square Footage */}
              <div className="mb-6">
                <h3 className="text-sm lg:text-base font-bold text-gray-600 mb-3">
                  Square Footage
                </h3>
                <SqftFilter
                  selectedMinSqft={tempFilters.sqftRange?.min}
                  selectedMaxSqft={tempFilters.sqftRange?.max}
                  onMinSqftChange={(value) =>
                    onTempFiltersChange({
                      ...tempFilters,
                      sqftRange: {
                        ...tempFilters.sqftRange,
                        min: value,
                      },
                    })
                  }
                  onMaxSqftChange={(value) =>
                    onTempFiltersChange({
                      ...tempFilters,
                      sqftRange: {
                        ...tempFilters.sqftRange,
                        max: value,
                      },
                    })
                  }
                  hideButtons
                  fullWidth
                />
              </div>
              <hr className="border-gray-200 my-6" />

              {/* Note */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p>
                  <span className="font-medium">Pro tip:</span> You can also use
                  natural language in your search! Try adding specific details
                  like "near downtown" or "with a large backyard" to find
                  exactly what you're looking for.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-4 p-4 sm:p-6 border-t mt-auto">
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 !bg-transparent"
              >
                Reset Filters
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="px-6 py-2 text-xs text-white rounded-lg"
                style={{ backgroundColor: "var(--primary)" }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>,
        portalRef.current
      )
    : null;
};

export default MoreFiltersModal;
